/**
 * @file Contains the core components of the app: map, input drawer and output drawer.
 */
import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useEffectOnce } from 'usehooks-ts';
import { useDrag } from '@use-gesture/react';
import { ref } from 'valtio';
import { grey } from '@mui/material/colors';
import { Grid, Box, SwipeableDrawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import AppContainer from './AppContainer';
import SubAppContainer from './SubAppContainer';
import { uiState } from '../state';
import { notifyResizeEvent } from '../utils';
import { DRAWER_BLEEDING } from '../constants';
import { useLargeScreen } from '../hooks';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const getDefaultWidth = () => {
  if (window.matchMedia('(min-width: 1280px) and (max-width: 1440px)').matches) {
    return 410;
  } else if (window.matchMedia('(min-width: 1441px)').matches) {
    return 440;
  } else {
    return 325;
  }
};

function PullerContainer({
  children,
  drawerBleeding,
  position = 'left',
  setDrawer = null,
  isOpen = null,
  ...rest
}) {
  const largeScreen = useLargeScreen();
  const swipeRef = useRef(null);
  const swipeRefRight = useRef(null);
  const inputExpandRef = useRef(null);
  const snapInput = useSnapshot(uiState);

  const startTouch = (e) => {
    const startY = e.clientY || e.touches[0].clientY;
    const currentHeight = uiState.outputRef.current.offsetHeight;

    const moveTouch = (e) => {
      const currentY = e.clientY || (e.touches && e.touches[0].clientY);
      const diffY = currentY - startY;
      const newHeight = currentHeight - diffY;
      if (uiState.outputRef) {
        requestAnimationFrame(() => {
          const box = document.querySelector('#legend-container');
          box.style.display = 'none';
          uiState.outputRef.current.style.maxHeight = '420px';
          uiState.outputRef.current.style.height = `${Math.max(Math.min(newHeight, 420), 100)}px`;
        });
      }
    };

    const endTouch = () => {
      swipeRef.current.removeEventListener('touchmove', moveTouch);
      swipeRef.current.removeEventListener('touchend', endTouch);
    };

    swipeRef.current.addEventListener('touchmove', moveTouch, { passive: false });
    swipeRef.current.addEventListener('touchend', endTouch, { passive: false });
  };

  const startMouseTouch = (e) => {
    const startX = e.clientX || e.touches[0].clientX;
    let currentWidth = uiState.outputRef.current.offsetWidth;

    const moveMouseTouch = (e) => {
      const currentX = e.clientX || (e.touches && e.touches[0].clientX);
      const diffX = currentX - startX;
      const newWidth = currentWidth - diffX;
      if (uiState.outputRef) {
        requestAnimationFrame(() => {
          uiState.outputRef.current.style.width = `${Math.max(
            Math.min(newWidth, 600),
            getDefaultWidth(),
          )}px`;
        });
      }
    };

    const endMouseTouch = (e) => {
      if (
        startX === (e.clientX || (e.changedTouches && e.changedTouches[0].clientX)) &&
        setDrawer
      ) {
        setDrawer((oldState) => !oldState);
      } else {
        setDrawer(true);
      }
      notifyResizeEvent({ chartResizeOnly: true });
      swipeRefRight.current.removeEventListener('mousemove', moveMouseTouch);
      swipeRefRight.current.removeEventListener('mouseup', endMouseTouch);
    };

    swipeRefRight.current.addEventListener('mousemove', moveMouseTouch, { passive: false });
    swipeRefRight.current.addEventListener('mouseup', endMouseTouch, { passive: false });
  };

  const expandInput = () => {
    if (uiState.inputRef && uiState.inputRef.current) {
      if (uiState.isInputExpanded) {
        uiState.inputRef.current.style.height = '120px';
        inputExpandRef.current.style.bottom = `${-drawerBleeding}px`;
        uiState.isInputExpanded = !uiState.isInputExpanded;
      } else {
        uiState.inputRef.current.style.height = '100%';
        inputExpandRef.current.style.bottom = `${-drawerBleeding}px`;
        uiState.isInputExpanded = !uiState.isInputExpanded;
      }
    }
    notifyResizeEvent();
  };

  useEffect(() => {}, [snapInput.isInputExpanded, snapInput.inputRef]);
  useEffect(() => {
    if (inputExpandRef) uiState.inputExpandRef = ref(inputExpandRef);
  }, [inputExpandRef]);

  if (position === 'left' || position === 'right') {
    const addRightStartListener =
      position === 'right' && largeScreen
        ? { onMouseDown: startMouseTouch, ref: swipeRefRight }
        : null;
    return (
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: grey[200],
          ...(position === 'left' && { borderBottomRightRadius: 8, borderTopRightRadius: 8 }),
          ...(position === 'right' && { borderBottomLeftRadius: 8, borderTopLeftRadius: 8 }),
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'absolute',
          ...(position === 'left' && { right: -drawerBleeding }),
          ...(position === 'right' && { left: -drawerBleeding }),
          top: 0,
          visibility: 'visible',
          width: drawerBleeding,
          zIndex: 1,
          '&:hover': {
            cursor: position === 'right' && isOpen ? 'ew-resize' : 'pointer',
            backgroundColor: grey[100],
          },
          touchAction: 'none',
        }}
        {...addRightStartListener}
        {...rest}
      >
        {children}
      </Box>
    );
  } else {
    // top or bottom
    const addBottomStartListener =
      position === 'bottom' && !largeScreen ? { onTouchStart: startTouch, ref: swipeRef } : null;
    const addTopStartListener =
      position === 'top' && !largeScreen
        ? { onTouchStart: expandInput, ref: inputExpandRef }
        : null;
    return (
      <Box
        sx={{
          backgroundColor: grey[200],
          position: 'absolute',
          ...(position === 'bottom' && { top: -drawerBleeding }),
          ...(position === 'top' && {
            bottom: -drawerBleeding,
          }),
          ...(position === 'bottom' && { borderTopLeftRadius: 8, borderTopRightRadius: 8 }),
          ...(position === 'top' && { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }),
          visibility: 'visible',
          right: 0,
          left: 0,
          zIndex: 1,
          touchAction: 'none',
          height: drawerBleeding,
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: grey[100],
          },
          ...(position === 'top' &&
            !largeScreen && { display: 'flex', justifyContent: 'center', alignItems: 'center' }),
        }}
        data-position={position}
        {...addBottomStartListener}
        {...addTopStartListener}
        {...rest}
      >
        {position === 'top' && !largeScreen ? (
          uiState.isInputExpanded ? (
            <KeyboardArrowUpIcon />
          ) : (
            <KeyboardArrowDownIcon />
          )
        ) : (
          children
        )}
      </Box>
    );
  }
}

function Puller({ children, position = 'left', ...rest }) {
  if (position === 'left' || position === 'right') {
    return (
      <Box
        sx={{
          width: 6,
          height: '25vh',
          backgroundColor: grey[600],
          borderRadius: 3,
          position: 'absolute',
          top: 'calc(50% - 25vh / 2)',
        }}
        {...rest}
      >
        {children}
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          width: 30,
          height: 6,
          backgroundColor: grey[600],
          borderRadius: 3,
          position: 'absolute',
          top: 3,
          left: 'calc(50% - 15px)',
        }}
        {...rest}
      >
        {children}
      </Box>
    );
  }
}

function useDrawer(position) {
  const snap = useSnapshot(uiState.drawsers);
  const setDrawer = (newState) => {
    if (typeof newState === 'function') {
      // poor-man setState-like functional update
      uiState.drawsers[position] = newState(uiState.drawsers[position]);
    } else {
      uiState.drawsers[position] = newState;
    }
  };
  const drawer = snap[position];
  const bind = useDrag(({ down, movement: [mx, my] }) => {
    const reference = position === 'left' || position === 'right' ? mx : my;
    if (down || Math.abs(reference) < 20) {
      // hysteresis
      return;
    }
    // redraw map in case the slide area is reduced
    if (reference > 0) {
      notifyResizeEvent();
    }

    if (reference > 0 && (position === 'left' || position === 'top') && !drawer) {
      setDrawer(true);
    } else if (reference < 0 && (position === 'right' || position === 'bottom') && !drawer) {
      setDrawer(true);
    } else if (reference < 0 && (position === 'left' || position === 'top') && drawer) {
      setDrawer(false);
    } else if (reference > 0 && (position === 'right' || position === 'bottom') && drawer) {
      setDrawer(false);
    }
  });
  return [drawer, setDrawer, bind];
}

PullerContainer.propTypes = {
  children: PropTypes.any,
  drawerBleeding: PropTypes.number,
  bleedingModified: PropTypes.number,
  setDrawer: PropTypes.func,
  isOpen: PropTypes.bool,
  position: PropTypes.string,
};

Puller.propTypes = {
  children: PropTypes.any,
  position: PropTypes.string,
};

MapApplication.propTypes = {
  inputs: PropTypes.any,
  outputs: PropTypes.any,
  children: PropTypes.any,
  inputsMd: PropTypes.string,
  inputsXs: PropTypes.string,
  outputsMd: PropTypes.string,
  outputsXs: PropTypes.string,
  drawerDefaultLeft: PropTypes.bool,
  drawerDefaultBottom: PropTypes.bool,
  drawerDefaultRight: PropTypes.bool,
  drawerDefaultTop: PropTypes.bool,
  childApp: PropTypes.bool,
};

/**
 *
 * @returns CADS Application layout for map based applications
 */
export default function MapApplication({
  inputs = null,
  outputs = null,
  children,
  inputsMd = 'left',
  inputsXs = 'bottom',
  outputsMd = 'left',
  outputsXs = 'bottom',
  drawerDefaultLeft = false,
  drawerDefaultBottom = false,
  drawerDefaultRight = false,
  drawerDefaultTop = false,
  childApp = false,
}) {
  const theme = useTheme();
  const largeScreen = useLargeScreen();
  const [drawerLeft, setDrawerLeft, bindLeft] = useDrawer('left');
  const [drawerBottom, setDrawerBottom, bindBottom] = useDrawer('bottom');
  const [drawerRight, setDrawerRight, bindRight] = useDrawer('right');
  const [drawerTop, setDrawerTop, bindTop] = useDrawer('top');

  const leftDrawerRef = useRef(null);
  const bottomDrawerRef = useRef(null);
  const rightDrawerRef = useRef(null);
  const topDrawerRef = useRef(null);

  useEffectOnce(() => {
    uiState.drawsers.left = drawerDefaultLeft;
    uiState.drawsers.bottom = drawerDefaultBottom;
    uiState.drawsers.right = drawerDefaultRight;
    uiState.drawsers.top = drawerDefaultTop;

    //open automatically childApp
    const drawerStateMap = {
      left: setDrawerLeft,
      right: setDrawerRight,
      top: setDrawerTop,
      bottom: setDrawerBottom,
    };
    if (childApp && largeScreen && drawerStateMap[outputsMd]) {
      drawerStateMap[outputsMd](true);
    } else if (childApp && !largeScreen && drawerStateMap[outputsXs]) {
      drawerStateMap[outputsXs](true); // Set the default state for small screens
    }
  });

  useEffectOnce(() => {
    if (leftDrawerRef.current) {
      leftDrawerRef.current.addEventListener('transitionend', notifyResizeEvent);
    }
    if (bottomDrawerRef.current) {
      bottomDrawerRef.current.addEventListener('transitionend', notifyResizeEvent);
    }
    if (rightDrawerRef.current) {
      rightDrawerRef.current.addEventListener('transitionend', notifyResizeEvent);
    }
    if (topDrawerRef.current) {
      topDrawerRef.current.addEventListener('transitionend', notifyResizeEvent);
    }
    return () => {
      if (leftDrawerRef.current) {
        leftDrawerRef.current.removeEventListener('transitionend', notifyResizeEvent);
      }
      if (bottomDrawerRef.current) {
        bottomDrawerRef.current.removeEventListener('transitionend', notifyResizeEvent);
      }
      if (rightDrawerRef.current) {
        rightDrawerRef.current.removeEventListener('transitionend', notifyResizeEvent);
      }
      if (topDrawerRef.current) {
        topDrawerRef.current.removeEventListener('transitionend', notifyResizeEvent);
      }
    };
  });

  const inputsOnLeft =
    ((inputsMd === 'left' && largeScreen) || (inputsXs === 'left' && !largeScreen)) &&
    Boolean(inputs);
  const outputsOnLeft =
    ((outputsMd === 'left' && largeScreen) || (outputsXs === 'left' && !largeScreen)) &&
    Boolean(outputs);
  const somethingOnLeft = inputsOnLeft || outputsOnLeft;
  const inputsOnBottom =
    ((inputsMd === 'bottom' && largeScreen) || (inputsXs === 'bottom' && !largeScreen)) &&
    Boolean(inputs);
  const outputsOnBottom =
    ((outputsMd === 'bottom' && largeScreen) || (outputsXs === 'bottom' && !largeScreen)) &&
    Boolean(outputs);
  const somethingOnBottom = inputsOnBottom || outputsOnBottom;
  const inputsOnRight =
    ((inputsMd === 'right' && largeScreen) || (inputsXs === 'right' && !largeScreen)) &&
    Boolean(inputs);
  const outputsOnRight =
    ((outputsMd === 'right' && largeScreen) || (outputsXs === 'right' && !largeScreen)) &&
    Boolean(outputs);
  const somethingOnRight = inputsOnRight || outputsOnRight;
  const inputsOnTop =
    ((inputsMd === 'top' && largeScreen) || (inputsXs === 'top' && !largeScreen)) &&
    Boolean(inputs);
  const outputsOnTop =
    ((outputsMd === 'top' && largeScreen) || (outputsXs === 'top' && !largeScreen)) &&
    Boolean(outputs);
  const somethingOnTop = inputsOnTop || outputsOnTop;

  const drawerContainerStylesL = {
    display: 'flex',
    flexDirection: 'column',
    gap: largeScreen ? theme.spacing(2) : theme.spacing(1),
    padding: theme.spacing(2, 0),
    width: '100%',
    maxWidth: '23dvw',
  };

  const drawerContainerStylesR = {
    display: 'flex',
    flexDirection: 'column',
    gap: largeScreen ? theme.spacing(2) : theme.spacing(1),
    padding: theme.spacing(2, 0),
    width: '100%',
  };

  const drawerContainerStylesTB = {
    display: 'flex',
    flexDirection: 'column',
    gap: largeScreen ? theme.spacing(2) : theme.spacing(1),
    padding: theme.spacing(2, 0),
    width: '100%',
    maxHeight: '40dvh',
  };

  const verticalButtons = useMediaQuery(`(max-width:${theme.custom.verticalInputsBreakpoint}px)`);

  const swipeableDrawerLeft = somethingOnLeft ? (
    <SwipeableDrawer
      data-drawer="left"
      transitionDuration={0}
      data-child-app={outputsOnLeft}
      aria-expanded={drawerLeft}
      open={drawerLeft}
      variant="persistent"
      anchor="left"
      ref={leftDrawerRef}
      onClose={() => {
        setDrawerLeft(false);
        notifyResizeEvent();
      }}
      onOpen={() => {
        setDrawerLeft(true);
        notifyResizeEvent();
      }}
      swipeAreaWidth={DRAWER_BLEEDING}
      disableSwipeToOpen={false}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '.MuiPaper-root': {
          overflow: 'visible',
          width: verticalButtons ? '256px' : 'auto',
        },
      }}
    >
      <PullerContainer
        position="left"
        drawerBleeding={DRAWER_BLEEDING}
        role="separator"
        aria-orientation="horizontal"
        tabIndex="0"
        onClick={() => {
          setDrawerLeft((oldState) => !oldState);
          notifyResizeEvent();
        }}
        {...bindLeft()}
      >
        <Puller position="left" />
      </PullerContainer>
      <SubAppContainer
        fullHeight
        sx={{
          backgroundColor: 'white',
          overflowY: 'auto',
        }}
      >
        <Box sx={drawerContainerStylesL}>
          {inputsOnLeft ? inputs : null}
          {outputsOnLeft ? outputs : null}
        </Box>
      </SubAppContainer>
    </SwipeableDrawer>
  ) : null;

  const swipeableDrawerRight = somethingOnRight ? (
    <SwipeableDrawer
      data-drawer="right"
      transitionDuration={0}
      data-child-app={outputsOnRight}
      aria-expanded={drawerRight}
      open={drawerRight}
      variant="persistent"
      anchor="right"
      ref={rightDrawerRef}
      onClose={() => {
        setDrawerRight(false);
        notifyResizeEvent();
      }}
      onOpen={() => {
        setDrawerRight(true);
        notifyResizeEvent();
      }}
      swipeAreaWidth={DRAWER_BLEEDING}
      disableSwipeToOpen={false}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '.MuiPaper-root': {
          overflow: 'visible',
        },
      }}
    >
      <PullerContainer
        position="right"
        drawerBleeding={DRAWER_BLEEDING}
        role="separator"
        aria-orientation="horizontal"
        tabIndex="0"
        onClick={() => {
          notifyResizeEvent({ chartResizeOnly: true });
        }}
        setDrawer={setDrawerRight}
        isOpen={drawerRight}
        {...bindRight()}
      >
        <Puller position="right" />
      </PullerContainer>
      <SubAppContainer
        fullHeight
        sx={{
          backgroundColor: 'white',
          overflowY: 'auto',
          width: `${getDefaultWidth()}px`,
        }}
        position="right"
      >
        <Box sx={drawerContainerStylesR}>
          {inputsOnRight ? inputs : null}
          {outputsOnRight ? outputs : null}
        </Box>
      </SubAppContainer>
    </SwipeableDrawer>
  ) : null;

  const swipeableDrawerBottom = somethingOnBottom ? (
    <SwipeableDrawer
      data-drawer="bottom"
      transitionDuration={0}
      data-child-app={outputsOnBottom}
      aria-expanded={drawerBottom}
      open={drawerBottom}
      variant="persistent"
      anchor="bottom"
      ref={bottomDrawerRef}
      onClose={() => {
        setDrawerBottom(false);
        notifyResizeEvent();
      }}
      onOpen={() => {
        setDrawerBottom(true);
        notifyResizeEvent();
      }}
      swipeAreaWidth={DRAWER_BLEEDING}
      disableSwipeToOpen={false}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '.MuiPaper-root': {
          overflow: 'visible',
          //height: `calc(50% - ${DRAWER_BLEEDING}px)`, //decrease the height on mobile
          minHeight: '150px',
        },
      }}
    >
      <PullerContainer
        position="bottom"
        drawerBleeding={DRAWER_BLEEDING}
        role="separator"
        bleedingModified={10}
        aria-orientation="vertical"
        tabIndex="0"
        onClick={() => {
          setDrawerBottom((oldState) => !oldState);
          notifyResizeEvent();
        }}
        {...bindBottom()}
      >
        <Puller position="bottom" />
      </PullerContainer>
      <SubAppContainer
        sx={{
          backgroundColor: 'white',
          overflowY: 'auto',
        }}
        position="bottom"
      >
        <Box sx={drawerContainerStylesTB}>
          {inputsOnBottom ? inputs : null}
          {outputsOnBottom ? outputs : null}
        </Box>
      </SubAppContainer>
    </SwipeableDrawer>
  ) : null;

  const swipeableDrawerTop = somethingOnTop ? (
    <SwipeableDrawer
      data-drawer="top"
      transitionDuration={0}
      data-child-app={outputsOnTop}
      aria-expanded={drawerTop}
      open={true}
      variant="persistent"
      anchor="top"
      ref={topDrawerRef}
      onClose={() => {
        uiState.inputRef.current.style.height = '120px';
      }}
      onOpen={() => {}}
      swipeAreaWidth={DRAWER_BLEEDING}
      disableSwipeToOpen={false}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '.MuiPaper-root': {
          overflow: 'visible',
          //height: `calc(40% - ${DRAWER_BLEEDING}px)`, //decrease the height on mobile
          minHeight: '150px',
        },
      }}
    >
      <PullerContainer
        position="top"
        drawerBleeding={DRAWER_BLEEDING}
        role="separator"
        bleedingModified={10}
        aria-orientation="vertical"
        tabIndex="0"
        onClick={() => {
          setDrawerTop((oldState) => !oldState);
          notifyResizeEvent();
        }}
        {...bindTop()}
      >
        <Puller position="top" />
      </PullerContainer>
      <SubAppContainer
        sx={{
          backgroundColor: 'white',
          overflowY: 'auto',
        }}
        position="top"
      >
        <Box sx={drawerContainerStylesTB}>
          {inputsOnTop ? inputs : null}
          {outputsOnTop ? outputs : null}
        </Box>
      </SubAppContainer>
    </SwipeableDrawer>
  ) : null;

  return (
    <>
      {swipeableDrawerLeft}
      {swipeableDrawerRight}
      {swipeableDrawerBottom}
      {swipeableDrawerTop}
      <AppContainer>
        <Grid sx={{ flexGrow: 1 }}>{children}</Grid>
      </AppContainer>
    </>
  );
}

MapApplication.propTypes = {
  inputsMd: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  inputsXs: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  outputsMd: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  outputsXs: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  drawerDefaultLeft: PropTypes.bool,
  drawerDefaultTop: PropTypes.bool,
  drawerDefaultRight: PropTypes.bool,
  drawerDefaultBottom: PropTypes.bool,
};
